'use client';

import { websiteEnv } from '@/constants/website-env';
import { useAppContext } from '@/context/app';
import useIsMobile from '@/hooks/use-is-mobile';
import { useSyncCart } from '@/hooks/use-sync-cart';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef, useState } from 'react';
import SearchBarLazyLoad from '../SearchBar/search-bar-lazy-load';
import CartTrigger from './cartTrigger';
import NavTrigger from './navTrigger';
import RecordInfluencer from './record-influencer';

const CountrySwitcher = dynamic(() => import('./country-switcher'), {
  ssr: false,
  loading: () => <div className='h-5 w-[22px] lg_w-[46px]' />,
});

let prevHeadStickedOnTop = true;

const HeaderToolbar = props => {
  const { miniCartOpen, setMiniCartOpen, setShowNoItemDrawer, announcements, headerId } = props;

  const isMobile = useIsMobile();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const headerToolbarRef = useRef<HTMLDivElement>(null);
  const mobileHeaderMenuRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [searchText, setSearchText] = useState('');

  const [
    { mobileFilterBarFixedOnTop, activeColumn, cartActiveColumn },
    {
      actions: { toggleHeadStickedOnTop, setHeaderToolbarHeight, setHeaderHeight, setActiveColumn },
    },
  ] = useAppContext();
  const realActiveColumn = pathname === '/cart' ? cartActiveColumn : activeColumn;

  useEffect(() => {
    const calculateHeaderRect = () => {
      const headerToolBarElement = headerToolbarRef.current;
      const mobileHeaderMenu = mobileHeaderMenuRef.current;
      const header = document.getElementById(headerId);

      if (headerToolBarElement) {
        setHeaderToolbarHeight(
          headerToolBarElement.offsetHeight -
            (window.matchMedia('(max-width: 959px)').matches ? mobileHeaderMenu?.offsetHeight || 0 : 0) || 0,
        );
      }

      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    calculateHeaderRect();

    const resizeHandler = debounce(() => calculateHeaderRect, 500);

    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [setHeaderToolbarHeight, isMobile, pathname, searchParams, setHeaderHeight, headerId]);

  useEffect(() => {
    const isStickedOnTop = window.scrollY === 0;

    if (prevHeadStickedOnTop === isStickedOnTop) return;

    toggleHeadStickedOnTop(isStickedOnTop);
    prevHeadStickedOnTop = isStickedOnTop;
  }, [pathname, searchParams, toggleHeadStickedOnTop]);

  useEffect(() => {
    const scrollHandler = () => {
      const isStickedOnTop = window.scrollY === 0;

      if (prevHeadStickedOnTop === isStickedOnTop) return;

      toggleHeadStickedOnTop(isStickedOnTop);
      prevHeadStickedOnTop = isStickedOnTop;
    };

    document.addEventListener('scroll', scrollHandler);

    return () => {
      document.removeEventListener('scroll', scrollHandler);
    };
  }, [toggleHeadStickedOnTop]);

  useEffect(() => {
    if (pathname === '/') {
      setActiveColumn('home');
      return;
    }

    if (pathname === '/attire') {
      setActiveColumn('attire');
    }
  }, [pathname, setActiveColumn]);

  useSyncCart();

  return (
    <div
      className={clsx(
        'after_pointer-events-none after_absolute after_bottom-0 after_left-0 after_h-[1px] after_w-full after_bg-[#eee]',
        isSearchOpen ? 'lg_after_opacity-0' : '',
        mobileFilterBarFixedOnTop && 'invisible after_invisible',
        'lg_visible lg_after_visible',
      )}
      ref={headerToolbarRef}
    >
      <Suspense fallback={null}>
        <RecordInfluencer />
      </Suspense>
      {announcements}
      <div className='flex items-center border-b-1 border-solid border-E7DED5 lg_justify-between lg_pr-10'>
        <div className='flex flex-1 items-center  lg_flex-none'>
          <Link
            href='/'
            className={clsx(
              'flex h-10 flex-1 basis-[155px] items-center justify-center lg_h-[60px] lg_flex-none lg_basis-[170px] ',
              realActiveColumn === 'home' && 'bg-E7DED5',
            )}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='130'
              height='30'
              fill='none'
              viewBox='0 0 130 30'
              className='h-auto w-[103px] lg_w-[130px]'
            >
              <path
                fill='#BE9983'
                fillRule='evenodd'
                d='M23.661 21.941c.585-.055 1.429-.495 1.85-.978.463-.523.681-1.184.082-1.639-.476-.358-1.224-.055-1.55.262-.382.372-.613 1.185-1.552 1.24-1.075.069-1.741-.923-2.04-2.52-.014-.07-.028-.152-.042-.235-.15-.95-.176-2.094-.081-3.347.027-.44.081-.992.15-1.598.054-.454.19-1.363.367-2.713a7.7 7.7 0 0 0-1.497.578c-.163.097-.34.207-.544.33l-.435.304q-.123.992-.245 2.107c-.45.042-4.055.042-5.28 0 1.606-2.479 3.647-5.62 6.75-9.793-.219.882-.504 2.466-.83 4.766.652-.303 1.373-.551 2.19-.771l.245-.07c.108-.812.23-1.721.367-2.713.218-1.473.408-2.658.49-3.209A31 31 0 0 1 22.49 0c-1.401 1.212-3.143 1.143-5.388 1.308-4.422.372-7.048 2.686-7.048 5.73 0 1.075.45 1.722 1.225 1.722.585 0 .993-.523.993-.964 0-.592-.476-1.143-.476-1.79 0-.8.517-1.722 1.292-2.342 1.429-1.143 3.647-1.212 6.654-1.488-6.028 6.86-9.62 14.903-14.15 19.504-.926.936-1.77 1.515-2.695 1.515-.653 0-1.47-.317-1.905-.317-.653 0-.993.44-.993.992 0 .826.68 1.28 1.946 1.28 3.034 0 6.027-3.925 10.15-10.302 1.62-.028 5.17 0 6.068 0v.44c.055 1.323.34 2.755.83 3.775 1.184 2.465 2.735 3.057 4.667 2.878'
                clipRule='evenodd'
              ></path>
              <path
                fill='#BE9983'
                fillRule='evenodd'
                d='M14.286 15.438a6 6 0 0 0-.11 1.074c0 1.074.477 2.107 1.293 2.107.585 0 .926-.482.926-1.033 0-.578-.667-1.157-.762-2.135zM15.007 13.537h1.32c.598-1.13 1.564-2.231 2.775-2.879 1.633-.868 3.483-1.088 3.483-1.088s-.408 1.309-.734 3.113c-.395 2.176-.708 4.463-.708 6.06.136.4.327.827.517 1.06.313.373.572.428.749.428h.068c.231-.014.38-.11.558-.29.136-.123.299-.344.449-.55 0-.207.013-.4.027-.606.163-2.37.354-4.6.939-7.107.571-2.41 1.456-4.725 1.456-4.725s-.898.661-1.905.91c-1.47.357-4.423.894-6.681 2.561-1.007.758-1.783 1.984-2.313 3.113M45.39 4.875c-.639 0-1.156.455-1.156 1.074 0 .689.721 1.447.721 3.003 0 2.135-1.333 4.656-3.51 7.975-2.735 4.104-5.184 6.832-6.722 8.801.028-3.884.041-8.181.327-11.68.286-3.498.857-6.212.857-6.212s-2.054 1.515-3.592 3.83c-1.755 2.616-3.47 5.88-4.953 8.429a134 134 0 0 1-3.333 5.482s-.245-1.887-.408-3.017c-.19 0-.381.014-.558.014-.653 0-1.238-.11-1.77-.317.191 2.893.532 5.523.885 7.741 6.79-8.347 9.049-15.04 11.021-17.534a48.5 48.5 0 0 0-.68 6.763l.163 10.33c2.694-2.59 4.953-5.495 7.987-9.531 4.055-5.413 6.232-9.49 6.232-12.327-.014-1.777-.558-2.824-1.51-2.824'
                clipRule='evenodd'
              ></path>
              <path
                fill='#403732'
                fillRule='evenodd'
                d='M59.402 17.047c-.014 0-.014 0-.028-.013-.136-.028-.285-.042-.435-.055a17 17 0 0 0-.857-.028c.122-.028.231-.055.34-.083 1.075-.289 1.714-.909 1.714-1.928 0-1.64-1.428-2.19-3.823-2.19H54.57v.124c.558.027.667.193.667.84v7.713c0 .634-.109.813-.667.84v.11h3.674c2.354 0 3.837-.826 3.837-2.713-.014-1.515-.98-2.369-2.68-2.617m-.205-2.093c0 .73-.23 1.35-1.034 1.694-.408.179-.952.29-1.7.317h-.381v-4.036c2.544 0 3.115.854 3.115 2.025m-1.183 7.272h-1.919v-5.082H58c2.544 0 3.13 1.198 3.13 2.548s-.667 2.534-3.116 2.534M76.505 22.365c-.885-.151-.912-1.597-1.347-2.837-.408-1.143-1.293-1.556-2.177-1.694 1.728-.22 2.64-1.047 2.64-2.48 0-1.68-1.239-2.534-3.606-2.534h-2.94v.124c.504.028.613.207.613.813v7.727c0 .647-.109.813-.694.84v.11h2.19v-.096c-.516-.028-.625-.22-.625-.826v-3.623h.857c1.796 0 2.354 0 2.898 2.163.422 1.639.939 2.41 2 2.492.136.014.272 0 .395-.027v-.152a.5.5 0 0 1-.204 0m-5.932-4.641v-4.725h1.224c2.313 0 2.898 1.102 2.898 2.37 0 1.267-.585 2.369-2.898 2.369h-1.224zM83.935 12.96c.653 0 .775.151.775.84v7.713c0 .675-.122.827-.775.84v.11h2.421v-.11c-.653 0-.775-.165-.775-.84V13.8c0-.689.122-.826.775-.84v-.124h-2.421zM97.663 12.836h-4.11v.124c.64.014.763.151.763.84v7.713c0 .675-.123.827-.762.84v.11h4.109c3.388 0 5.265-2.162 5.265-4.82s-1.877-4.807-5.265-4.807m-.245 9.462h-2.232v-9.297h2.232c3.238 0 4.558 2.121 4.558 4.642 0 2.52-1.32 4.655-4.558 4.655M116.793 21.513l-3.388-8.099-.245-.578h-.164l-.381 1.005-2.925 7.672c-.231.579-.449.84-1.061.84h-.232v.11h2.3v-.11h-.272c-.694 0-.844-.151-.585-.84l.734-1.9.137-.359.19-.51.014-.027.381-1.005h2.952l.463 1.088.122.29.068.164.15.359.789 1.9c.286.661.327.84-.367.84h-.272v.11h2.939v-.11h-.15c-.694 0-.898-.165-1.197-.84m-5.443-3.925 1.347-3.554.163.372.749 1.777.598 1.405zM129.611 21.21c0 .703-.15 1.088-.83 1.088h-2.313V13.8c0-.689.122-.826.762-.84v-.124h-2.422v.124c.653.014.775.151.775.84v7.713c0 .675-.122.827-.775.84v.11h4.911v-1.377h-.108z'
                clipRule='evenodd'
              ></path>
            </svg>
          </Link>
          <Link
            href='/attire'
            className={clsx(
              'flex h-10 flex-1 basis-[220px] items-center justify-center lg_h-[60px] lg_flex-none lg_basis-[243px] ',
              realActiveColumn === 'attire' && 'bg-E7DED5',
            )}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='204'
              height='30'
              fill='none'
              viewBox='0 0 204 30'
              className='h-auto w-[162px] lg_w-[204px]'
            >
              <path
                fill='#BE9983'
                fillRule='evenodd'
                d='M24.381 21.941c.585-.055 1.429-.495 1.85-.978.463-.523.68-1.184.082-1.639-.476-.358-1.224-.055-1.551.262-.38.372-.612 1.185-1.551 1.24-1.075.069-1.742-.923-2.041-2.52-.014-.07-.027-.152-.04-.235-.15-.95-.178-2.094-.082-3.347.027-.44.081-.992.15-1.598.054-.454.19-1.363.367-2.713a7.7 7.7 0 0 0-1.497.578c-.163.097-.34.207-.544.33l-.436.304q-.122.992-.245 2.107c-.449.042-4.054.042-5.279 0 1.606-2.479 3.646-5.62 6.749-9.793-.218.882-.504 2.466-.83 4.766.653-.303 1.374-.551 2.19-.771l.245-.07c.11-.812.232-1.721.368-2.713.217-1.473.408-2.658.49-3.209A31 31 0 0 1 23.21 0c-1.401 1.212-3.143 1.143-5.388 1.308-4.422.372-7.048 2.686-7.048 5.73 0 1.075.449 1.722 1.224 1.722.585 0 .994-.523.994-.964 0-.592-.477-1.143-.477-1.79 0-.8.517-1.722 1.293-2.342 1.429-1.143 3.647-1.212 6.653-1.488-6.027 6.86-9.62 14.903-14.15 19.504-.925.936-1.769 1.515-2.694 1.515-.653 0-1.47-.317-1.905-.317-.653 0-.993.44-.993.992 0 .826.68 1.28 1.945 1.28 3.035 0 6.028-3.925 10.15-10.302 1.62-.028 5.171 0 6.07 0v.44c.053 1.323.34 2.755.83 3.775 1.183 2.465 2.734 3.057 4.666 2.878'
                clipRule='evenodd'
              ></path>
              <path
                fill='#BE9983'
                fillRule='evenodd'
                d='M15.005 15.438a6 6 0 0 0-.109 1.074c0 1.074.477 2.107 1.293 2.107.585 0 .925-.482.925-1.033 0-.578-.666-1.157-.762-2.135zM15.727 13.537h1.319c.599-1.13 1.565-2.231 2.776-2.879 1.633-.868 3.483-1.088 3.483-1.088s-.408 1.309-.735 3.113c-.394 2.176-.707 4.463-.707 6.06.136.4.326.827.517 1.06.313.373.572.428.748.428h.068c.232-.014.381-.11.558-.29.136-.123.3-.344.45-.55 0-.207.013-.4.026-.606.164-2.37.354-4.6.94-7.107.57-2.41 1.455-4.725 1.455-4.725s-.898.661-1.905.91c-1.47.357-4.422.894-6.68 2.561-1.007.758-1.783 1.984-2.313 3.113M46.11 4.875c-.64 0-1.156.455-1.156 1.074 0 .689.721 1.447.721 3.003 0 2.135-1.333 4.656-3.51 7.975-2.735 4.104-5.184 6.832-6.722 8.801.027-3.884.04-8.181.327-11.68.285-3.498.857-6.212.857-6.212s-2.055 1.515-3.592 3.83c-1.755 2.616-3.47 5.88-4.953 8.429a134 134 0 0 1-3.334 5.482s-.244-1.887-.408-3.017c-.19 0-.38.014-.558.014a4.8 4.8 0 0 1-1.768-.317c.19 2.893.53 5.523.884 7.741 6.79-8.347 9.048-15.04 11.021-17.534a48.4 48.4 0 0 0-.68 6.763l.163 10.33c2.694-2.59 4.953-5.495 7.987-9.531 4.055-5.413 6.232-9.49 6.232-12.327-.014-1.777-.558-2.824-1.51-2.824'
                clipRule='evenodd'
              ></path>
              <path
                fill='#403732'
                fillRule='evenodd'
                d='M60.121 17.047c-.013 0-.013 0-.027-.013-.136-.028-.286-.042-.435-.055a17 17 0 0 0-.858-.028c.123-.028.232-.055.34-.083 1.076-.289 1.715-.909 1.715-1.928 0-1.64-1.429-2.19-3.823-2.19H55.29v.124c.558.027.667.193.667.84v7.713c0 .634-.11.813-.667.84v.11h3.674c2.354 0 3.837-.826 3.837-2.713-.014-1.515-.98-2.369-2.68-2.617m-.204-2.093c0 .73-.231 1.35-1.034 1.694-.408.179-.952.29-1.7.317H56.8v-4.036c2.545 0 3.116.854 3.116 2.025m-1.184 7.272h-1.918v-5.082h1.905c2.544 0 3.13 1.198 3.13 2.548s-.668 2.534-3.117 2.534M77.226 22.365c-.885-.151-.912-1.597-1.347-2.837-.409-1.143-1.293-1.556-2.178-1.694 1.729-.22 2.64-1.047 2.64-2.48 0-1.68-1.238-2.534-3.606-2.534h-2.939v.124c.504.028.613.207.613.813v7.727c0 .647-.11.813-.694.84v.11h2.19v-.096c-.517-.028-.625-.22-.625-.826v-3.623h.857c1.796 0 2.354 0 2.898 2.163.422 1.639.939 2.41 2 2.492.136.014.272 0 .395-.027v-.152a.5.5 0 0 1-.205 0m-5.933-4.641v-4.725h1.225c2.313 0 2.898 1.102 2.898 2.37 0 1.267-.585 2.369-2.898 2.369h-1.225zM84.654 12.96c.653 0 .776.151.776.84v7.713c0 .675-.123.827-.776.84v.11h2.422v-.11c-.653 0-.775-.165-.775-.84V13.8c0-.689.122-.826.775-.84v-.124h-2.422zM98.383 12.836h-4.11v.124c.64.014.762.151.762.84v7.713c0 .675-.122.827-.762.84v.11h4.11c3.388 0 5.265-2.162 5.265-4.82s-1.877-4.807-5.266-4.807m-.245 9.462h-2.232v-9.297h2.232c3.238 0 4.558 2.121 4.558 4.642 0 2.52-1.32 4.655-4.558 4.655M117.512 21.513l-3.388-8.099-.245-.578h-.163l-.381 1.005-2.925 7.672c-.232.579-.449.84-1.062.84h-.231v.11h2.3v-.11h-.272c-.694 0-.844-.151-.586-.84l.735-1.9.136-.359.191-.51.013-.027.381-1.005h2.953l.463 1.088.122.29.068.164.15.359.789 1.9c.286.661.326.84-.368.84h-.272v.11h2.939v-.11h-.149c-.694 0-.898-.165-1.198-.84m-5.442-3.925 1.347-3.554.163.372.748 1.777.599 1.405zM130.33 21.21c0 .703-.149 1.088-.83 1.088h-2.313V13.8c0-.689.123-.826.762-.84v-.124h-2.422v.124c.653.014.776.151.776.84v7.713c0 .675-.123.827-.776.84v.11h4.912v-1.377h-.109z'
                clipRule='evenodd'
              ></path>
              <path
                fill='#BE9983'
                d='M140.439 10h1v15h-1z'
              ></path>
              <path
                fill='#403732'
                d='M153.122 22.337h-1.683v-.024a.73.73 0 0 0 .5-.304l.378-.524q.22-.268.951-1.854.39-.829.671-1.45.28-.623.475-1.086a135 135 0 0 0 1.756-4.462q.926 2.22 1.841 4.328a215 215 0 0 0 1.841 4.133q.22.562.536.865.317.306.683.354v.049h-2.145q-.317-.78-.549-1.39l-.402-1.048h-3.974zm4.584-3.047a68 68 0 0 1-.451-1.085q-.426-1.037-1.28-3.085l-1.755 4.267h3.536zM171.019 13.008q-.025.658-.025 1.207v1.012h-.073q-.634-.927-1.048-1.244-.537-.39-1.878-.39h-.512q-.244.024-.292.122-.024.024-.024.122v6.852q0 .39.195.658.317.415.829.561l.025.024q-.05.05-.342.049-1.805-.074-2.779 0-.195 0-.269-.074.464-.024.793-.33.329-.303.353-.719v-7.02q0-.148-.146-.22h-.731q-1.342 0-1.878.39-.415.316-1.048 1.243h-.073v-1.012q0-.549-.025-1.207l3.829.024zM180.966 13.008q-.024.658-.024 1.207v1.012h-.074q-.634-.927-1.048-1.244-.537-.39-1.877-.39h-.512q-.244.024-.293.122-.024.024-.024.122v6.852q0 .39.195.658.317.415.83.561l.024.024q-.049.05-.342.049-1.805-.074-2.779 0-.196 0-.268-.074.463-.024.792-.33.33-.303.354-.719v-7.02q0-.148-.147-.22h-.731q-1.342 0-1.878.39-.414.316-1.048 1.243h-.073v-1.012q0-.549-.024-1.207l3.828.024zM185.379 12.986a1.2 1.2 0 0 0-.609.244q-.245.194-.44.512v6.997q0 .39.196.659.316.414.829.56l.024.025q-.049.05-.341.049-1.805-.073-2.78 0-.22 0-.292-.074a1.21 1.21 0 0 0 1.171-1.049v-6.753q0-.243-.122-.488a2 2 0 0 0-.305-.475q-.183-.208-.402-.208.073-.024 1.561-.024 1.485 0 1.51.025M186.403 21.949q.634-.122.732-.976V20.9q.024-.024.024-.196v-6.389q0-1.096-.78-1.243l3.487-.049q1.12.026 1.829.488.829.537.829 1.585 0 1.39-1.756 2.365-.195.05-.243.122 2.706 3.95 5.242 4.292l.488.073-2.244.025q-1.365 0-2.389-1.195a209 209 0 0 0-2.365-2.95q-.61-.001-.927.073l.025 2.804q0 1.098.78 1.244zm4.73-6.754q0-.902-.463-1.293-.488-.414-1.463-.414-.805 0-.829.244v.146l-.049 3.39h.561q1.17 0 1.707-.598.536-.597.536-1.475M203.473 21.986a1.1 1.1 0 0 1-.293.025 41 41 0 0 0-2.706-.074h-1.402q-.792 0-1.817.024.78-.12.78-1.243v-6.364q0-1.122-.78-1.243v-.025h2.536q.536 0 1.012-.012t.914-.036h.098a14 14 0 0 0 1.146-.049q.439-.024.439.074v1.414q-.05-.39-.5-.562-.452-.17-1.743-.268a9 9 0 0 0-.696-.024h-.914q-.22 0-.292.074-.024.024-.024.22v2.852q0 .146.195.146h.817q.646 0 1.792.024.56 0 .817-.256t.305-.597h.024q-.024.537 0 2.243-.147-.78-1.244-.78a31 31 0 0 0-1.085-.036 81 81 0 0 0-1.499-.012q-.123 0-.122.097v2.975q0 .634.731.78.122.024 1.268.024.512 0 .878-.097.853-.22 1.243-.512t.512-.634l-.268 1.78-.024.097z'
              ></path>
            </svg>
          </Link>
        </div>

        {isMobile ? null : (
          <div className='hidden lg_flex lg_items-center lg_gap-x-10'>
            <Link
              href='/swatches'
              className='group flex items-center gap-x-1.5'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='19'
                height='23'
                fill='none'
                viewBox='0 0 19 23'
              >
                <g
                  className='fill-#403732 lg_group-hover_fill-80583F'
                  clipPath='url(#clip0_12101_8630)'
                >
                  <path d='M16.328 22.032H1.878A1.879 1.879 0 010 20.156V5.867h18.207v14.29a1.879 1.879 0 01-1.879 1.875zM1.058 6.919v13.234c0 .454.37.82.82.82h14.447c.454 0 .821-.37.821-.82V6.92H1.057z' />
                  <path d='M18.207 6.919H0V1.907C0 .872.842.031 1.878.031h14.447c1.036 0 1.878.841 1.878 1.876V6.92h.004zM1.057 5.863H17.15V1.907c0-.454-.37-.82-.822-.82H1.878a.824.824 0 00-.82.82v3.956z' />
                  <path d='M15.114 5.86H3.074v1.055h12.04V5.86z' />
                  <path d='M3.125 19.3h-.017c-.18-.003-.444-.07-.688-.366-.518-.626-.12-1.281.141-1.71.166-.271.339-.553.339-.81 0-.27-.088-.384-.247-.584-.2-.254-.476-.598-.476-1.239 0-.64.275-.985.476-1.239.159-.2.247-.31.247-.584 0-.275-.088-.384-.247-.584-.2-.254-.476-.598-.476-1.239 0-.64.275-.985.476-1.239.159-.2.247-.31.247-.58 0-.272-.088-.384-.247-.585-.2-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238l.159-.201.257-.004h12.296l.159.201c.2.253.476.598.476 1.239 0 .64-.275.985-.476 1.239-.159.2-.247.31-.247.584 0 .27.088.384.247.584.2.254.476.598.476 1.239 0 .64-.275.985-.476 1.239-.159.2-.247.31-.247.584 0 .271.088.384.247.584.2.254.476.599.476 1.24 0 .608-.29.992-.5 1.27-.205.27-.268.366-.22.58.085.387.255.651.417.908.236.373.599.94.063 1.588-.243.295-.507.362-.687.366h-.018c-.433 0-.73-.331-.99-.623-.116-.13-.363-.405-.462-.405-.098 0-.148.042-.335.282-.211.277-.535.693-1.17.693-.634 0-.958-.419-1.17-.694-.187-.239-.236-.281-.335-.281-.098 0-.148.042-.334.282-.212.277-.536.693-1.17.693-.635 0-.96-.419-1.17-.694-.184-.239-.237-.281-.332-.281-.099 0-.148.042-.335.282-.211.277-.535.693-1.17.693-.638 0-.958-.419-1.173-.694-.187-.239-.236-.281-.335-.281-.099 0-.342.274-.462.405-.257.288-.553.62-.987.62zm7.472-2.083c.638 0 .959.418 1.17.693.183.24.236.282.335.282.099 0 .148-.043.335-.282.211-.278.535-.693 1.17-.693.546 0 .93.4 1.212.714-.003-.007-.01-.014-.014-.021-.183-.285-.43-.68-.557-1.25-.151-.696.173-1.126.409-1.439.187-.25.286-.39.286-.633 0-.271-.089-.384-.247-.585-.201-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238.158-.2.247-.31.247-.585 0-.27-.089-.383-.247-.584-.201-.253-.476-.598-.476-1.239 0-.64.275-.985.476-1.238.158-.201.247-.31.247-.585a.666.666 0 00-.1-.38H3.338a.664.664 0 00-.102.387c0 .271.088.384.246.585.201.253.476.598.476 1.238 0 .641-.275.986-.476 1.24-.158.2-.246.309-.246.583 0 .271.088.384.246.585.201.253.476.598.476 1.239 0 .64-.275.985-.476 1.238-.158.201-.246.31-.246.585 0 .274.088.383.246.584.201.253.476.598.476 1.239 0 .552-.275 1-.493 1.358-.025.042-.057.088-.085.141.282-.31.66-.693 1.195-.693.638 0 .958.418 1.173.693.187.24.237.281.335.281.099 0 .148-.042.335-.281.212-.278.536-.693 1.17-.693.635 0 .959.418 1.17.693.184.24.236.281.332.281.098 0 .148-.042.334-.281.215-.282.536-.7 1.174-.7zM15.508 2.25H8.48v1.056h7.028V2.25zM15.506 4.14h-3.514v1.056h3.514V4.141z' />
                </g>
                <defs>
                  <clipPath id='clip0_12101_8630'>
                    <path
                      fill='#fff'
                      d='M0 0H18.207V22H0z'
                      transform='translate(0 .031)'
                    />
                  </clipPath>
                </defs>
              </svg>
              <span className='text-sm leading-[19px] text-#403732 lg_group-hover_text-80583F'>
                Get 5 Free Swatches
              </span>
            </Link>
            <CountrySwitcher />
          </div>
        )}
      </div>
      <div
        className='mx-auto flex h-[66px] items-center justify-between gap-x-5 px-[15px] lg_hidden'
        ref={mobileHeaderMenuRef}
      >
        <NavTrigger />
        <div className='flex min-w-0 flex-1 items-center gap-x-2.5 rounded-[30px] bg-[#f8f8f8] px-2.5'>
          <input
            className='h-10 min-w-0 flex-1 rounded-none border-none bg-transparent text-[15px] outline-0 placeholder_text-[#929292] focus_!border-none'
            placeholder={websiteEnv.isUnitedKingdom ? 'Search by colour or name' : 'Search by color or name'}
            value={searchText}
            onChange={e => {
              setSearchText(e.target.value);
            }}
            autoComplete='off'
            onKeyUp={e => {
              if (e.key === 'Enter' && searchText.trim()) {
                setIsSearchOpen(true);
              }
            }}
          />
          <button
            type='button'
            className='flex-none'
            onClick={() => {
              setIsSearchOpen(true);
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='19'
              height='18'
              fill='none'
              viewBox='0 0 19 18'
            >
              <path
                fill='#80583F'
                fillRule='evenodd'
                d='M9.173 15.506c2.023 0 3.843-.81 5.124-2.158 1.28-1.28 2.157-3.1 2.157-5.123s-.809-3.843-2.157-5.124C13.016 1.821 11.196.944 9.173.944S5.331 1.753 4.05 3.1C2.769 4.382 1.892 6.202 1.892 8.225s.81 3.842 2.158 5.123c1.28 1.349 3.1 2.158 5.123 2.158m6.135-1.82 3.64 3.64-.674.674-3.64-3.64c-1.483 1.28-3.37 2.09-5.46 2.09-2.293 0-4.315-.944-5.799-2.427S.948 10.449.948 8.225c0-2.292.944-4.315 2.427-5.798S6.948 0 9.173 0c2.292 0 4.315.944 5.798 2.427S17.398 6 17.398 8.225c0 2.09-.81 4.045-2.09 5.46'
                clipRule='evenodd'
              ></path>
            </svg>
          </button>
        </div>
        <div className='flex flex-none items-center gap-x-[15px]'>
          <CountrySwitcher />
          <CartTrigger
            miniCartOpen={miniCartOpen}
            setMiniCartOpen={setMiniCartOpen}
            setShowNoItemDrawer={setShowNoItemDrawer}
          />
        </div>
      </div>
      {isMobile ? (
        <SearchBarLazyLoad
          isSearchOpen={isSearchOpen}
          setIsSearchOpen={setIsSearchOpen}
          searchText={searchText}
        />
      ) : null}
    </div>
  );
};

export default HeaderToolbar;
